import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarPlus, FileSignature, AlertTriangle, DollarSign, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { SERVICE_MAP, STATUS_META } from "@/data/services";
import { DeadlinePill } from "@/components/DeadlineWatch";

const StatusPill = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.submitted;
  return <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${m.cls}`}>{m.label}</span>;
};

const Metric = ({ icon: Icon, label, value, accent }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${accent}`}><Icon className="w-5 h-5" /></div>
    <p className="font-sans font-bold text-3xl text-brand-navy tabular-nums" style={{ fontVariantNumeric: "lining-nums tabular-nums" }}>{value}</p>
    <p className="text-sm text-slate-500 mt-0.5">{label}</p>
  </div>
);

const AppRow = ({ a }) => (
  <Link to={`/admin/applications/${a.id}`} data-testid={`admin-home-row-${a.id}`}
    className="group flex items-center justify-between gap-4 px-5 py-3.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
    <div className="min-w-0">
      <p className="text-sm text-brand-navy font-medium truncate">{SERVICE_MAP[a.service_type]?.title || a.service_type}</p>
      <p className="text-xs text-slate-400 font-mono truncate">{a.reference_no} · {a.applicant_name || a.applicant_email}</p>
    </div>
    <div className="flex items-center gap-2 shrink-0">
      {a.needs_action && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-brand-red border border-red-200" data-testid="action-badge">
          <AlertTriangle className="w-3 h-3" /> Action
        </span>
      )}
      <DeadlinePill app={a} />
      <StatusPill status={a.status} />
      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-brand-red group-hover:translate-x-1 transition-all" />
    </div>
  </Link>
);

const ListCard = ({ title, testid, apps, empty }) => (
  <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={testid}>
    <div className="px-5 py-3.5 border-b border-slate-100 font-semibold text-brand-navy text-sm flex items-center justify-between">
      <span>{title}</span>
      <span className="text-xs font-normal text-slate-400">{apps.length}</span>
    </div>
    {apps.length === 0 ? (
      <p className="px-5 py-8 text-center text-sm text-slate-400">{empty}</p>
    ) : apps.map((a) => <AppRow key={a.id} a={a} />)}
  </div>
);

export default function AdminHome() {
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([api.get("/admin/stats"), api.get("/applications")]);
        setStats(s.data); setApps(a.data);
      } catch (e) { toast.error(apiErr(e)); setApps([]); }
    })();
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const todayApps = useMemo(() => (apps || []).filter((a) => (a.created_at || "").slice(0, 10) === today), [apps, today]);
  const pendingQuotes = useMemo(() => (apps || []).filter((a) => a.status === "submitted"), [apps]);
  const actionNeeded = useMemo(() => (apps || []).filter((a) => a.needs_action), [apps]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-brand-red font-bold">Admin Portal</p>
          <h1 className="font-serif text-4xl text-brand-navy tracking-tight mt-1">Today at ImmiLaw.VN</h1>
        </div>
        <Link to="/admin/applications" data-testid="view-all-applications-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-navy text-white text-sm font-semibold hover:bg-slate-800 transition-colors">
          View all applications <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {apps === null ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-brand-red animate-spin" /></div>
      ) : (
        <>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Metric icon={CalendarPlus} label="New today" value={todayApps.length} accent="bg-brand-red/10 text-brand-red" />
            <Metric icon={FileSignature} label="Pending quotes" value={pendingQuotes.length} accent="bg-amber-100 text-brand-amber" />
            <Metric icon={AlertTriangle} label="Action needed" value={actionNeeded.length} accent="bg-red-100 text-brand-red" />
            <Metric icon={DollarSign} label="Revenue (USD)" value={stats ? Number(stats.revenue).toLocaleString() : "—"} accent="bg-emerald-100 text-emerald-600" />
          </motion.div>

          <div className="mt-8 grid lg:grid-cols-2 gap-6">
            <ListCard title="New applications today" testid="today-applications" apps={todayApps} empty="No new applications today." />
            <ListCard title="Awaiting quotation" testid="pending-quotes" apps={pendingQuotes} empty="No applications awaiting a quotation." />
          </div>

          {actionNeeded.length > 0 && (
            <div className="mt-6">
              <ListCard title="Flagged — action needed" testid="action-needed-list" apps={actionNeeded} empty="" />
            </div>
          )}
        </>
      )}
    </div>
  );
}
